import { type ProjectColor } from '@apm/contracts';

/**
 * Lớp Tailwind của từng màu dự án — APM-PSET-e.
 *
 * ## ⚠️ Vì sao viết sẵn cả bảng thay vì ghép `'bg-project-' + color`
 *
 * Tailwind quét **mã nguồn** để biết sinh lớp nào, nên một tên lớp ghép lúc
 * chạy không có rule nào: nhãn hiện đúng chữ, đúng bố cục, và **trong suốt**.
 * Bẫy này đã ghi ở CLAUDE.md và đã cắn hai lần (`bg-tag-` ở APM-1303, biến thể
 * `motion-safe:group-hover/label:` ở APM-PSET-d). Ba mươi dòng lặp là cái giá
 * của việc lớp thật sự tồn tại.
 *
 * ## Ba mức, ba chỗ dùng
 *
 * - `dot` — màu **đặc**: chấm chọn màu và vạch dọc đầu hàng thanh bên. Ở đây
 *   màu là *hình*, không phải nền chữ, nên nó được đặc.
 * - `soft` — **nền đặc** + chữ trắng/đen theo độ sáng của màu (ngưỡng 0.6, cùng
 *   `solidTagStyle` của loại việc): mã dự án ở thanh bên, thẻ timesheet. Đổi từ
 *   nền nhạt ngày 2026-09-25 — người dùng báo màu dự án khó phân biệt.
 * - `border` — **viền** cùng màu với nền của `soft`: khung thẻ timesheet
 *   (người dùng yêu cầu 2026-09-30 — nền đặc chỉ còn ở mã việc, khung thẻ mang
 *   viền 1px). Theo màu của `soft` chứ không theo tên khoá: vài khoá mượn màu
 *   của khoá khác, và viền lệch màu với mã việc ngay bên trong trông như lỗi.
 * - `tab` — chỉ nền, **không** đụng màu chữ: nhãn tab Điều lệ mang tên dự án và
 *   nó phải đọc như chữ bình thường. Đậm hơn khi tab đang mở, vì lớp
 *   `data-[state=active]:bg-background` mặc định sẽ xoá hẳn màu đi.
 */
export const PROJECT_COLOR_CLASS: Record<
  ProjectColor,
  {
    readonly dot: string;
    readonly soft: string;
    readonly border: string;
    readonly tab: string;
  }
> = {
  red: {
    dot: 'bg-project-red',
    soft: 'bg-project-red text-white',
    border: 'border-project-red',
    tab: 'bg-project-red/15 data-[state=active]:bg-project-red/30',
  },
  orange: {
    dot: 'bg-project-orange',
    soft: 'bg-project-orange text-white',
    border: 'border-project-orange',
    tab: 'bg-project-orange/15 data-[state=active]:bg-project-orange/30',
  },
  amber: {
    dot: 'bg-project-amber',
    soft: 'bg-project-amber text-white',
    border: 'border-project-amber',
    tab: 'bg-project-amber/15 data-[state=active]:bg-project-amber/30',
  },
  gold: {
    dot: 'bg-project-gold',
    soft: 'bg-project-gold text-white',
    border: 'border-project-gold',
    tab: 'bg-project-gold/15 data-[state=active]:bg-project-gold/30',
  },
  lime: {
    dot: 'bg-project-lime',
    soft: 'bg-project-lime text-white',
    border: 'border-project-lime',
    tab: 'bg-project-lime/15 data-[state=active]:bg-project-lime/30',
  },
  green: {
    dot: 'bg-project-green',
    soft: 'bg-project-green text-white',
    border: 'border-project-green',
    tab: 'bg-project-green/15 data-[state=active]:bg-project-green/30',
  },
  emerald: {
    dot: 'bg-project-emerald',
    soft: 'bg-project-emerald text-white',
    border: 'border-project-emerald',
    tab: 'bg-project-emerald/15 data-[state=active]:bg-project-emerald/30',
  },
  teal: {
    dot: 'bg-project-teal',
    soft: 'bg-project-teal text-white',
    border: 'border-project-teal',
    tab: 'bg-project-teal/15 data-[state=active]:bg-project-teal/30',
  },
  cyan: {
    dot: 'bg-project-cyan',
    soft: 'bg-project-cyan text-white',
    border: 'border-project-cyan',
    tab: 'bg-project-cyan/15 data-[state=active]:bg-project-cyan/30',
  },
  sky: {
    dot: 'bg-project-sky',
    soft: 'bg-project-sky text-white',
    border: 'border-project-sky',
    tab: 'bg-project-sky/15 data-[state=active]:bg-project-sky/30',
  },
  blue: {
    dot: 'bg-project-blue',
    soft: 'bg-project-blue text-white',
    border: 'border-project-blue',
    tab: 'bg-project-blue/15 data-[state=active]:bg-project-blue/30',
  },
  indigo: {
    dot: 'bg-project-indigo',
    soft: 'bg-project-indigo text-white',
    border: 'border-project-indigo',
    tab: 'bg-project-indigo/15 data-[state=active]:bg-project-indigo/30',
  },
  violet: {
    dot: 'bg-project-violet',
    soft: 'bg-project-violet text-white',
    border: 'border-project-violet',
    tab: 'bg-project-violet/15 data-[state=active]:bg-project-violet/30',
  },
  purple: {
    dot: 'bg-project-purple',
    soft: 'bg-project-purple text-white',
    border: 'border-project-purple',
    tab: 'bg-project-purple/15 data-[state=active]:bg-project-purple/30',
  },
  magenta: {
    dot: 'bg-project-magenta',
    soft: 'bg-project-magenta text-white',
    border: 'border-project-magenta',
    tab: 'bg-project-magenta/15 data-[state=active]:bg-project-magenta/30',
  },
  pink: {
    dot: 'bg-project-pink',
    soft: 'bg-project-pink text-white',
    border: 'border-project-pink',
    tab: 'bg-project-pink/15 data-[state=active]:bg-project-pink/30',
  },
  rose: {
    dot: 'bg-project-rose',
    soft: 'bg-project-rose text-white',
    border: 'border-project-rose',
    tab: 'bg-project-rose/15 data-[state=active]:bg-project-rose/30',
  },
  stone: {
    dot: 'bg-project-stone',
    soft: 'bg-project-stone text-white',
    border: 'border-project-stone',
    tab: 'bg-project-stone/15 data-[state=active]:bg-project-stone/30',
  },
  slate: {
    dot: 'bg-project-slate',
    soft: 'bg-project-slate text-white',
    border: 'border-project-slate',
    tab: 'bg-project-slate/15 data-[state=active]:bg-project-slate/30',
  },
  graphite: {
    dot: 'bg-project-graphite',
    soft: 'bg-project-graphite text-white',
    border: 'border-project-graphite',
    tab: 'bg-project-graphite/15 data-[state=active]:bg-project-graphite/30',
  },
  blush: {
    dot: 'bg-project-blush',
    soft: 'bg-project-blush text-gray-900',
    border: 'border-project-blush',
    tab: 'bg-project-blush/15 data-[state=active]:bg-project-blush/30',
  },
  peach: {
    dot: 'bg-project-peach',
    soft: 'bg-project-peach text-gray-900',
    border: 'border-project-peach',
    tab: 'bg-project-peach/15 data-[state=active]:bg-project-peach/30',
  },
  yellow: {
    dot: 'bg-project-yellow',
    soft: 'bg-project-yellow text-gray-900',
    border: 'border-project-yellow',
    tab: 'bg-project-yellow/15 data-[state=active]:bg-project-yellow/30',
  },
  olive: {
    dot: 'bg-project-olive',
    soft: 'bg-project-olive text-gray-900',
    border: 'border-project-olive',
    tab: 'bg-project-olive/15 data-[state=active]:bg-project-olive/30',
  },
  mint: {
    dot: 'bg-project-mint',
    soft: 'bg-project-mint text-gray-900',
    border: 'border-project-mint',
    tab: 'bg-project-mint/15 data-[state=active]:bg-project-mint/30',
  },
  aqua: {
    dot: 'bg-project-aqua',
    soft: 'bg-project-aqua text-gray-900',
    border: 'border-project-aqua',
    tab: 'bg-project-aqua/15 data-[state=active]:bg-project-aqua/30',
  },
  ice: {
    dot: 'bg-project-ice',
    soft: 'bg-project-ice text-gray-900',
    border: 'border-project-ice',
    tab: 'bg-project-ice/15 data-[state=active]:bg-project-ice/30',
  },
  azure: {
    dot: 'bg-project-azure',
    soft: 'bg-project-azure text-gray-900',
    border: 'border-project-azure',
    tab: 'bg-project-azure/15 data-[state=active]:bg-project-azure/30',
  },
  lavender: {
    dot: 'bg-project-lavender',
    soft: 'bg-project-lavender text-gray-900',
    border: 'border-project-lavender',
    tab: 'bg-project-lavender/15 data-[state=active]:bg-project-lavender/30',
  },
  candy: {
    dot: 'bg-project-candy',
    soft: 'bg-project-candy text-gray-900',
    border: 'border-project-candy',
    tab: 'bg-project-candy/15 data-[state=active]:bg-project-candy/30',
  },
};

/** Diện mạo của một dự án **chưa chọn màu** — cũng là lối thoát cho tên màu lạ. */
const NO_COLOR = {
  dot: 'bg-transparent',
  soft: 'text-muted-foreground',
  border: 'border-border',
  tab: '',
} as const;

/**
 * Lớp của một màu dự án, **chịu được giá trị lạ** — APM-PSET-e.
 *
 * ## ⚠️ Vì sao không tra thẳng `PROJECT_COLOR_CLASS[color]`
 *
 * Bản đầu viết `color === null ? … : PROJECT_COLOR_CLASS[color].dot`, và nó nổ
 * ngay ở test với `TypeError: Cannot read properties of undefined (reading
 * 'dot')`: giá trị tới nơi là `undefined` chứ không phải `null`, nên nhánh
 * `=== null` trượt và phép tra trả về `undefined`.
 *
 * Đó không phải chuyện của riêng test. Cùng giá trị ấy tới được từ đời thật:
 * một phản hồi `/projects` React Query còn giữ trong cache từ **trước** lần
 * deploy thêm trường này, hay một tên màu vừa bị gỡ khỏi bảng trong khi cơ sở
 * dữ liệu vẫn còn hàng mang nó. Và cái giá thì hoàn toàn lệch với thứ đang
 * hỏng: một trường **trang trí** ném lỗi lúc render làm trắng cả thanh bên —
 * tức mất luôn đường đi lại giữa các dự án, vì một ô màu.
 *
 * Tên lạ ⇒ coi như chưa chọn màu, đúng như `toView` ở server đã làm với cột
 * `VARCHAR` tự do.
 */
export function projectColorClass(color: string | null | undefined): {
  readonly dot: string;
  readonly soft: string;
  readonly border: string;
  readonly tab: string;
} {
  if (color === null || color === undefined) return NO_COLOR;
  return PROJECT_COLOR_CLASS[color as ProjectColor] ?? NO_COLOR;
}

/** Lớp nền của nhãn tab Điều lệ — chuỗi rỗng khi dự án chưa chọn màu. */
export function tabColorClass(color: string | null | undefined): string {
  return projectColorClass(color).tab;
}
